import { createFileRoute, Link } from "@tanstack/react-router";
import {
	AlertCircle,
	ArrowLeft,
	Building,
	Calendar,
	CreditCard,
	DollarSign,
	Edit,
	Image,
	Plus,
	Star,
	Trash2,
	Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export const Route = createFileRoute(
	"/_auth/app/_app/help/user-guide/customer-plans",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation();

	return (
		<div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6 sm:space-y-8">
			{/* Mobile Back Button */}
			<Link
				to="/app/help/user-guide"
				className="md:hidden flex items-center gap-2 text-primary"
			>
				<ArrowLeft className="w-4 h-4" />
				{t("common.back")}
			</Link>

			{/* Header */}
			<div className="mb-6 sm:mb-8">
				<div className="flex items-center space-x-3 mb-4">
					<div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
						<CreditCard className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
					</div>
					<div className="min-w-0">
						<h1 className="text-xl sm:text-3xl font-bold text-base-content">
							{t("help.customerPlans")}
						</h1>
						<p className="text-sm sm:text-lg text-base-content/70">
							{t("help.customerPlansDescription")}
						</p>
					</div>
				</div>
			</div>

			{/* Overview */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<CreditCard className="h-5 w-5" />
						<span>{t("help.customerPlansOverview")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.customerPlansOverviewDescription")}
					</p>
					<div className="bg-info/10 border border-info/20 rounded-lg p-4">
						<div className="flex items-start space-x-3">
							<AlertCircle className="h-5 w-5 text-info mt-0.5" />
							<div>
								<h4 className="font-medium text-info">
									{t("help.customerPlansKeyFeatures")}
								</h4>
								<ul className="text-sm text-info/80 mt-2 space-y-1">
									<li>• {t("help.customerPlansFeature1")}</li>
									<li>• {t("help.customerPlansFeature2")}</li>
									<li>• {t("help.customerPlansFeature3")}</li>
									<li>• {t("help.customerPlansFeature4")}</li>
								</ul>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Accessing Customer Plans */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<CreditCard className="h-5 w-5" />
						<span>{t("help.customerPlansAccessing")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.customerPlansAccessingDescription")}
					</p>
					<ol className="list-decimal list-inside space-y-2 text-base-content/80">
						<li>{t("help.customerPlansAccessingStep1")}</li>
						<li>{t("help.customerPlansAccessingStep2")}</li>
						<li>{t("help.customerPlansAccessingStep3")}</li>
					</ol>
					<div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
						<div className="flex items-start space-x-3">
							<AlertCircle className="h-5 w-5 text-warning mt-0.5" />
							<div>
								<h4 className="font-medium text-warning">
									{t("help.customerPlansPermissionRequirements")}
								</h4>
								<p className="text-sm text-warning/80 mt-1">
									{t("help.customerPlansPermissionDescription")}
								</p>
							</div>
						</div>
					</div>
					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.customerPlansImagePlaceholder1")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Plan Structure and Components */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Star className="h-5 w-5" />
						<span>{t("help.customerPlanStructure")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.customerPlanStructureDescription")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerPlanBasicInfo")}
							</h4>
							<div className="grid md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.customerPlanName")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPlanNameDescription")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.customerPlanDescription")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPlanDescriptionText")}
										</p>
									</div>
								</div>
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.customerPlanStatus")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPlanStatusDescription")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.customerPlanServicesOffered")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPlanServicesDescription")}
										</p>
									</div>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerPlanPricingBilling")}
							</h4>
							<div className="grid md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content flex items-center">
											<DollarSign className="h-4 w-4 mr-2" />
											{t("help.customerPlanPriceCurrency")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPlanPriceCurrencyDescription")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content flex items-center">
											<Calendar className="h-4 w-4 mr-2" />
											{t("help.customerPlanBillingCycle")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPlanBillingCycleDescription")}
										</p>
									</div>
								</div>
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.customerPlanMonthlyAverage")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPlanMonthlyAverageDescription")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.customerPlanSessionLimits")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPlanSessionLimitsDescription")}
										</p>
									</div>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerPlanEstablishmentAccess")}
							</h4>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.customerPlanAllEstablishments")}</li>
								<li>• {t("help.customerPlanSpecificEstablishments")}</li>
								<li>• {t("help.customerPlanMultiLocationSupport")}</li>
							</ul>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.customerPlansImagePlaceholder2")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Creating New Customer Plans */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Plus className="h-5 w-5" />
						<span>{t("help.customerPlansCreating")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.customerPlansCreatingDescription")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerPlansStep1")}
							</h4>
							<ol className="list-decimal list-inside space-y-1 text-sm text-base-content/70">
								<li>{t("help.customerPlansStep1Detail1")}</li>
								<li>{t("help.customerPlansStep1Detail2")}</li>
								<li>{t("help.customerPlansStep1Detail3")}</li>
							</ol>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerPlansStep2")}
							</h4>
							<div className="space-y-2">
								<h5 className="font-medium text-sm text-base-content">
									{t("help.customerPlansRequiredFields")}
								</h5>
								<ul className="text-sm text-base-content/70 space-y-1">
									<li>• {t("help.customerPlansNameField")}</li>
									<li>• {t("help.customerPlansDescriptionField")}</li>
								</ul>
								<div className="bg-success/10 border border-success/20 rounded-lg p-3">
									<p className="text-sm text-success/80">
										{t("help.customerPlansTip")}
									</p>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerPlansStep3")}
							</h4>
							<div className="space-y-2">
								<h5 className="font-medium text-sm text-base-content">
									{t("help.customerPlansPricingConfiguration")}
								</h5>
								<ul className="text-sm text-base-content/70 space-y-1">
									<li>• {t("help.customerPlansPriceField")}</li>
									<li>• {t("help.customerPlansCurrencyField")}</li>
									<li>• {t("help.customerPlansBillingCycleField")}</li>
									<li>• {t("help.customerPlansSessionsLimitField")}</li>
								</ul>
								<div className="bg-info/10 border border-info/20 rounded-lg p-3">
									<p className="text-sm text-info/80">
										{t("help.customerPlansMonthlyAverageNote")}
									</p>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerPlansStep4")}
							</h4>
							<div className="space-y-2">
								<h5 className="font-medium text-sm text-base-content">
									{t("help.customerPlansFeatureManagement")}
								</h5>
								<ul className="text-sm text-base-content/70 space-y-1">
									<li>• {t("help.customerPlansAddFeatures")}</li>
									<li>• {t("help.customerPlansRemoveFeatures")}</li>
									<li>• {t("help.customerPlansMinimumRequirement")}</li>
									<li>• {t("help.customerPlansExamples")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerPlansStep5")}
							</h4>
							<div className="space-y-2">
								<h5 className="font-medium text-sm text-base-content">
									{t("help.customerPlansLocationAccess")}
								</h5>
								<ul className="text-sm text-base-content/70 space-y-1">
									<li>• {t("help.customerPlansAllEstablishmentsOption")}</li>
									<li>
										• {t("help.customerPlansSpecificEstablishmentsOption")}
									</li>
									<li>• {t("help.customerPlansMultiSelect")}</li>
									<li>• {t("help.customerPlansClearSelection")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerPlansStep6")}
							</h4>
							<div className="space-y-2">
								<h5 className="font-medium text-sm text-base-content">
									{t("help.customerPlansActivationControl")}
								</h5>
								<ul className="text-sm text-base-content/70 space-y-1">
									<li>• {t("help.customerPlansActivePlans")}</li>
									<li>• {t("help.customerPlansInactivePlans")}</li>
									<li>• {t("help.customerPlansDefault")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerPlansStep7")}
							</h4>
							<ol className="list-decimal list-inside space-y-1 text-sm text-base-content/70">
								<li>{t("help.customerPlansSaveStep1")}</li>
								<li>{t("help.customerPlansSaveStep2")}</li>
								<li>{t("help.customerPlansSaveStep3")}</li>
								<li>{t("help.customerPlansSaveStep4")}</li>
							</ol>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.customerPlansImagePlaceholder3")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Managing Existing Plans */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Edit className="h-5 w-5" />
						<span>{t("help.customerPlansManaging")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.customerPlansManagingDescription")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerPlanListOverview")}
							</h4>
							<div className="grid md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.customerPlanTableColumns")}
										</h5>
										<ul className="text-sm text-base-content/70 space-y-1">
											<li>• {t("help.customerPlanNameColumn")}</li>
											<li>• {t("help.customerPlanDescriptionColumn")}</li>
											<li>• {t("help.customerPlanPriceColumn")}</li>
											<li>• {t("help.customerPlanBillingCycleColumn")}</li>
										</ul>
									</div>
								</div>
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.customerPlanAdditionalInfo")}
										</h5>
										<ul className="text-sm text-base-content/70 space-y-1">
											<li>• {t("help.customerPlanServicesColumn")}</li>
											<li>• {t("help.customerPlanEstablishmentsColumn")}</li>
											<li>• {t("help.customerPlanSessionsColumn")}</li>
											<li>• {t("help.customerPlanStatusColumn")}</li>
										</ul>
									</div>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerPlanAvailableActions")}
							</h4>
							<div className="space-y-2">
								<div>
									<h5 className="font-medium text-sm text-base-content flex items-center">
										<Edit className="h-4 w-4 mr-2" />
										{t("help.customerPlanEditAction")}
									</h5>
									<p className="text-sm text-base-content/70">
										{t("help.customerPlanEditDescription")}
									</p>
								</div>
								<div>
									<h5 className="font-medium text-sm text-base-content flex items-center">
										<Trash2 className="h-4 w-4 mr-2" />
										{t("help.customerPlanDeleteAction")}
									</h5>
									<p className="text-sm text-base-content/70">
										{t("help.customerPlanDeleteDescription")}
									</p>
								</div>
							</div>
						</div>

						<div className="bg-error/10 border border-error/20 rounded-lg p-4">
							<div className="flex items-start space-x-3">
								<AlertCircle className="h-5 w-5 text-error mt-0.5" />
								<div>
									<h5 className="font-medium text-error">
										{t("help.customerPlanDeletionRestrictions")}
									</h5>
									<p className="text-sm text-error/80 mt-1">
										{t("help.customerPlanDeletionRestrictionsDescription")}
									</p>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.customerPlansImagePlaceholder4")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Billing Cycles and Pricing Strategy */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Calendar className="h-5 w-5" />
						<span>{t("help.customerPlansBillingCycles")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.customerPlansBillingCyclesDescription")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerPlansAvailableBillingCycles")}
							</h4>
							<div className="grid md:grid-cols-2 gap-4">
								<div className="space-y-3">
									<div>
										<h5 className="font-medium text-sm text-base-content flex items-center">
											<Calendar className="h-4 w-4 mr-2 text-green-600" />
											{t("help.customerPlansMonthlyBilling")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPlansMonthlyDescription")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content flex items-center">
											<Calendar className="h-4 w-4 mr-2 text-blue-600" />
											{t("help.customerPlansQuarterlyBilling")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPlansQuarterlyDescription")}
										</p>
									</div>
								</div>
								<div className="space-y-3">
									<div>
										<h5 className="font-medium text-sm text-base-content flex items-center">
											<Calendar className="h-4 w-4 mr-2 text-purple-600" />
											{t("help.customerPlansSemiAnnualBilling")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPlansSemiAnnualDescription")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content flex items-center">
											<Calendar className="h-4 w-4 mr-2 text-orange-600" />
											{t("help.customerPlansAnnualBilling")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPlansAnnualDescription")}
										</p>
									</div>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerPlansMonthlyAverageDisplay")}
							</h4>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.customerPlansMonthlyAverageFeature1")}</li>
								<li>• {t("help.customerPlansMonthlyAverageFeature2")}</li>
								<li>• {t("help.customerPlansMonthlyAverageFeature3")}</li>
								<li>• {t("help.customerPlansMonthlyAverageFeature4")}</li>
							</ul>
						</div>

						<div className="bg-success/10 border border-success/20 rounded-lg p-4">
							<div className="flex items-start space-x-3">
								<DollarSign className="h-5 w-5 text-success mt-0.5" />
								<div>
									<h5 className="font-medium text-success">
										{t("help.customerPlansPricingStrategyTips")}
									</h5>
									<ul className="text-sm text-success/80 mt-2 space-y-1">
										<li>• {t("help.customerPlansPricingTip1")}</li>
										<li>• {t("help.customerPlansPricingTip2")}</li>
										<li>• {t("help.customerPlansPricingTip3")}</li>
										<li>• {t("help.customerPlansPricingTip4")}</li>
									</ul>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.customerPlansImagePlaceholder5")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Session Limits and Capacity Management */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Users className="h-5 w-5" />
						<span>{t("help.customerPlansSessionLimits")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.customerPlansSessionLimitsDescription")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerPlansUnderstandingSessionLimits")}
							</h4>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.customerPlansOptionalFeature")}</li>
								<li>• {t("help.customerPlansRange")}</li>
								<li>• {t("help.customerPlansEnforcement")}</li>
								<li>• {t("help.customerPlansFlexibility")}</li>
							</ul>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerPlansStrategicUses")}
							</h4>
							<div className="grid md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.customerPlansCapacityManagement")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPlansCapacityDescription")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.customerPlansTieredPricing")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPlansTieredDescription")}
										</p>
									</div>
								</div>
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.customerPlansRevenueOptimization")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPlansRevenueDescription")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.customerPlansFairAccess")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPlansFairAccessDescription")}
										</p>
									</div>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerPlansCommonStrategies")}
							</h4>
							<div className="space-y-2">
								<div className="bg-base-100 border border-base-200 rounded-lg p-3">
									<h5 className="font-medium text-sm text-base-content mb-2">
										{t("help.customerPlansExampleStructure")}
									</h5>
									<ul className="text-sm text-base-content/70 space-y-1">
										<li>• {t("help.customerPlansStarterPlan")}</li>
										<li>• {t("help.customerPlansRegularPlan")}</li>
										<li>• {t("help.customerPlansPremiumPlan")}</li>
										<li>• {t("help.customerPlansUnlimitedPlan")}</li>
									</ul>
								</div>
							</div>
						</div>

						<div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
							<div className="flex items-start space-x-3">
								<AlertCircle className="h-5 w-5 text-warning mt-0.5" />
								<div>
									<h5 className="font-medium text-warning">
										{t("help.customerPlansImplementationConsiderations")}
									</h5>
									<ul className="text-sm text-warning/80 mt-2 space-y-1">
										<li>• {t("help.customerPlansMonitorUsage")}</li>
										<li>• {t("help.customerPlansPeakHours")}</li>
										<li>• {t("help.customerPlansCommunicateLimits")}</li>
										<li>• {t("help.customerPlansReviewAdjust")}</li>
									</ul>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.customerPlansImagePlaceholder6")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Multi-Establishment Plans */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Building className="h-5 w-5" />
						<span>{t("help.customerPlansMultiEstablishment")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.customerPlansMultiEstablishmentDescription")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerPlansEstablishmentAccessOptions")}
							</h4>
							<div className="space-y-3">
								<div>
									<h5 className="font-medium text-sm text-base-content">
										{t("help.customerPlansAllEstablishmentsDefault")}
									</h5>
									<ul className="text-sm text-base-content/70 space-y-1">
										<li>
											• {t("help.customerPlansAllEstablishmentsFeature1")}
										</li>
										<li>
											• {t("help.customerPlansAllEstablishmentsFeature2")}
										</li>
										<li>
											• {t("help.customerPlansAllEstablishmentsFeature3")}
										</li>
										<li>
											• {t("help.customerPlansAllEstablishmentsFeature4")}
										</li>
									</ul>
								</div>
								<div>
									<h5 className="font-medium text-sm text-base-content">
										{t("help.customerPlansSpecificEstablishmentsTitle")}
									</h5>
									<ul className="text-sm text-base-content/70 space-y-1">
										<li>• {t("help.customerPlansSpecificFeature1")}</li>
										<li>• {t("help.customerPlansSpecificFeature2")}</li>
										<li>• {t("help.customerPlansSpecificFeature3")}</li>
										<li>• {t("help.customerPlansSpecificFeature4")}</li>
									</ul>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerPlansUseCases")}
							</h4>
							<div className="grid md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.customerPlansPremiumLocations")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPlansPremiumDescription")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.customerPlansGeographicPricing")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPlansGeographicDescription")}
										</p>
									</div>
								</div>
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.customerPlansCapacityManagementMulti")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPlansCapacityMultiDescription")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.customerPlansSpecializedServices")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPlansSpecializedDescription")}
										</p>
									</div>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerPlansManagingAssignments")}
							</h4>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.customerPlansCheckboxInterface")}</li>
								<li>• {t("help.customerPlansBadgesDisplay")}</li>
								<li>• {t("help.customerPlansRemoveIndividual")}</li>
								<li>• {t("help.customerPlansClearAll")}</li>
							</ul>
						</div>

						<div className="bg-info/10 border border-info/20 rounded-lg p-4">
							<div className="flex items-start space-x-3">
								<Building className="h-5 w-5 text-info mt-0.5" />
								<div>
									<h5 className="font-medium text-info">
										{t("help.customerPlansBestPractices")}
									</h5>
									<ul className="text-sm text-info/80 mt-2 space-y-1">
										<li>• {t("help.customerPlansStartSimple")}</li>
										<li>• {t("help.customerPlansAddRestrictions")}</li>
										<li>• {t("help.customerPlansCommunicateRestrictions")}</li>
										<li>• {t("help.customerPlansOfferBoth")}</li>
									</ul>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.customerPlansImagePlaceholder7")}</em>
						</p>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
